// src/pages/BotPage.js
import React, { useEffect, useMemo, useState } from "react";
import {
    getBotSettings,
    updateBotSettings,
    getBotStatus,
    getBotLogs,
    runBotTick,
    killSwitch,
    refreshBotState,
    resetBotState,
    runSmartExit,
    getTargetBotSettings,
    updateTargetBotSettings,
    getTargetBotStatus,
    getTargetBotLogs,
    runTargetBotTick,
    resetTargetBotState,
    getAdminDiagnosticsCatalog,
    downloadAdminDiagnosticsZip,
} from "../api/botApi";
import { getAuthToken } from "../apiClient";
import PutBotPanel from "./PutBotPanel";
import SwingBotPanel from "./SwingBotPanel";
import CryptoSwingBotPanel from "./CryptoSwingBotPanel";
import CryptoIgnitionBotPanel from "./CryptoIgnitionBotPanel";
import StockIgnitionBotPanel from "./StockIgnitionBotPanel";
import CryptoBotPanel from '../components/CryptoBotPanel';


const TABS = ["Bot Control", "Targeted Bot", "PUT Bot", "Swing Bot", "Stock Ignition Bot", "Crypto Bot", "Crypto Swing Bot", "Crypto Ignition Bot", "Risk Level", "Smart Exit", "Live Status", "Logs", "Diagnostics Export", "Kill Switch"];
const API_BASE = process.env.REACT_APP_API_BASE || "https://mystockpicks.onrender.com";

const LIQUID_PRESET = [
    "AAPL", "MSFT", "NVDA", "AMZN", "GOOGL", "META", "TSLA", "AMD",
    "PLTR", "AVGO", "NFLX", "SPY", "QQQ", "IWM", "SOXL", "JPM",
    "XOM", "WMT", "BAC", "CRM"
];

function getUserIdFromToken() {
    try {
        const token = getAuthToken();
        if (!token) return "";
        const parts = token.split(".");
        if (parts.length < 2) return "";
        const payload = JSON.parse(atob(parts[1]));
        return (payload?.email || "").toLowerCase();
    } catch (e) {
        return "";
    }
}

function Pill({ children }) {
    return (
        <span className="px-3 py-1 rounded-full text-xs bg-gray-800 border border-gray-700">
            {children}
        </span>
    );
}

function parseWatchlistText(text) {
    return Array.from(
        new Set(
            String(text || "")
                .split(/[\s,\n\r\t,]+/)
                .map((s) => s.trim().toUpperCase())
                .filter(Boolean)
        )
    );
}

function watchlistToText(watchlist) {
    return (watchlist || []).join(", ");
}

export default function BotPage() {
    const [active, setActive] = useState("Bot Control");
    const [settings, setSettings] = useState(null);
    const [status, setStatus] = useState(null);
    const [logs, setLogs] = useState([]);
    const [topScan, setTopScan] = useState(null);
    const [tradeActivity, setTradeActivity] = useState({ orders: [], count: 0 });
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [watchlistText, setWatchlistText] = useState("");
    const [smartExitResult, setSmartExitResult] = useState(null);
    const [targetSettings, setTargetSettings] = useState(null);
    const [targetStatus, setTargetStatus] = useState(null);
    const [targetLogs, setTargetLogs] = useState([]);
    const [targetResult, setTargetResult] = useState(null);

    const userId = getUserIdFromToken();

    async function fetchTopScanResults(uid) {
        try {
            const token = getAuthToken();
            const res = await fetch(`${API_BASE}/api/bot/top-trades?user_id=${encodeURIComponent(uid)}&top=10`, {
                method: "GET",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.error || `Top scan failed (${res.status})`);
            setTopScan(data);
        } catch (e) {
            setTopScan({ error: e.message || String(e), top_trades: [] });
        }
    }

    async function fetchTradeActivity(uid) {
        try {
            const token = getAuthToken();
            const res = await fetch(
                `${API_BASE}/api/bot/trade-forensics?user_id=${encodeURIComponent(uid)}&limit=100`,
                {
                    method: "GET",
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.error || `Trade activity failed (${res.status})`);
            setTradeActivity(data || { orders: [], count: 0 });
        } catch (e) {
            setTradeActivity({ orders: [], count: 0, error: e.message || String(e) });
        }
    }

    async function refreshAll() {
        if (!userId) {
            setError("Missing user session. Please log in again.");
            return;
        }
        setError("");
        try {
            const s = await getBotSettings(userId);
            setSettings(s);
            setWatchlistText(watchlistToText(s?.watchlist || []));

            const st = await getBotStatus(userId);
            setStatus(st);

            const lg = await getBotLogs(userId, 200);
            setLogs(lg.logs || []);

            await fetchTopScanResults(userId);
            await fetchTradeActivity(userId);

            const ts = await getTargetBotSettings(userId);
            setTargetSettings(ts);
            const tst = await getTargetBotStatus(userId);
            setTargetStatus(tst);
            const tl = await getTargetBotLogs(userId, 100);
            setTargetLogs(tl.logs || []);
        } catch (e) {
            setError(e.message || String(e));
        }
    }

    useEffect(() => {
        refreshAll();
    }, [userId]);

    const profiles = useMemo(() => settings?.profiles || {}, [settings]);
    const supportedBrokers = useMemo(() => settings?.supported_brokers || [], [settings]);

    async function patch(p) {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            const s = await updateBotSettings(userId, p);
            setSettings(s);
            if (Array.isArray(s?.watchlist)) {
                setWatchlistText(watchlistToText(s.watchlist));
            }
            const st = await getBotStatus(userId);
            setStatus(st);
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function saveWatchlist(list) {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            const cleaned = Array.isArray(list) ? list : parseWatchlistText(watchlistText);
            const s = await updateBotSettings(userId, { watchlist: cleaned });
            setSettings(s);
            setWatchlistText(watchlistToText(s?.watchlist || cleaned));
            const st = await getBotStatus(userId);
            setStatus(st);
            const lg = await getBotLogs(userId, 200);
            setLogs(lg.logs || []);

            await fetchTopScanResults(userId);
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function runOneTick() {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            await runBotTick(userId);
            await refreshAll();
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function doRefreshState() {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            await refreshBotState(userId);
            await refreshAll();
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function doResetState(clearPeak = false) {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            await resetBotState(userId, clearPeak);
            await refreshAll();
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function doSmartExit(execute = false) {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            const mode = settings?.paper_mode ? "paper" : "live";
            const result = await runSmartExit(userId, { mode, execute, debug: true });
            setSmartExitResult(result);
            const lg = await getBotLogs(userId, 200);
            setLogs(lg.logs || []);
            const st = await getBotStatus(userId);
            setStatus(st);
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function patchTargetBot(p) {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            const s = await updateTargetBotSettings(userId, p);
            setTargetSettings(s);
            const st = await getTargetBotStatus(userId);
            setTargetStatus(st);
            const lg = await getTargetBotLogs(userId, 100);
            setTargetLogs(lg.logs || []);
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function runTargetTick(execute = true, sellOnly = false) {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            const result = await runTargetBotTick(userId, { ignore_cooldown: false, execute, sell_only: sellOnly });
            setTargetResult(result);
            const st = await getTargetBotStatus(userId);
            setTargetStatus(st);
            const lg = await getTargetBotLogs(userId, 100);
            setTargetLogs(lg.logs || []);
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function resetTargetState() {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            await resetTargetBotState(userId);
            const st = await getTargetBotStatus(userId);
            setTargetStatus(st);
            const lg = await getTargetBotLogs(userId, 100);
            setTargetLogs(lg.logs || []);
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function doKill(disable = true) {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            await killSwitch(userId, !disable);
            await refreshAll();
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    const enabled = Boolean(settings?.enabled);

    return (
        <div className="flex gap-4">
            <div className="w-56 shrink-0">
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-2">
                    <div className="text-sm font-semibold mb-2">AI Bot</div>
                    <div className="flex flex-col gap-1">
                        {TABS.map((t) => (
                            <button
                                key={t}
                                onClick={() => setActive(t)}
                                className={`text-left px-3 py-2 rounded-lg text-sm ${active === t
                                    ? "bg-gray-800 border border-gray-700"
                                    : "hover:bg-gray-800/50"
                                    }`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>

                    <div className="mt-4 flex flex-col gap-2">
                        <div className="flex items-center justify-between text-xs text-gray-300">
                            <span>Status</span>
                            <Pill>{enabled ? "ON" : "OFF"}</Pill>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-300">
                            <span>Broker</span>
                            <Pill>{settings?.broker || "-"}</Pill>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-300">
                            <span>Risk</span>
                            <Pill>{settings?.risk_level || "-"}</Pill>
                        </div>
                    </div>
                </div>

                {error ? <div className="mt-3 text-red-400 text-sm">{error}</div> : null}
            </div>

            <div className="flex-1">
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-lg font-semibold">{active}</div>
                            <div className="text-xs text-gray-400">
                                When Bot Lock is ON, manual trading endpoints should return 409 to prevent conflicts.
                            </div>
                        </div>

                        <div className="flex gap-2 flex-wrap">
                            <button
                                disabled={busy}
                                onClick={refreshAll}
                                className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm"
                            >
                                Refresh
                            </button>

                            <button
                                disabled={busy}
                                onClick={doRefreshState}
                                className="px-3 py-2 rounded-lg bg-blue-900/30 border border-blue-700 text-sm"
                            >
                                Refresh State
                            </button>

                            <button
                                disabled={busy}
                                onClick={() => doResetState(false)}
                                className="px-3 py-2 rounded-lg bg-yellow-900/30 border border-yellow-700 text-sm"
                            >
                                Reset State
                            </button>

                            <button
                                disabled={busy}
                                onClick={runOneTick}
                                className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm"
                            >
                                Run 1 Tick
                            </button>
                        </div>
                    </div>

                    <DebugSummary settings={settings} status={status} />

                    <TopScanPanel topScan={topScan} refreshTopScan={() => fetchTopScanResults(userId)} busy={busy} />

                    <TradeActivityPanel activity={tradeActivity} refreshActivity={() => fetchTradeActivity(userId)} busy={busy} />

                    <div className="mt-4">
                        {active === "Bot Control" && (
                            <BotControl
                                settings={settings}
                                busy={busy}
                                supportedBrokers={supportedBrokers}
                                patch={patch}
                                watchlistText={watchlistText}
                                setWatchlistText={setWatchlistText}
                                saveWatchlist={saveWatchlist}
                            />
                        )}
                        {active === "Targeted Bot" && (
                            <TargetedBotPanel
                                settings={targetSettings}
                                status={targetStatus}
                                logs={targetLogs}
                                result={targetResult}
                                busy={busy}
                                patch={patchTargetBot}
                                runTick={runTargetTick}
                                resetState={resetTargetState}
                            />
                        )}
                        {active === "PUT Bot" && (
                            <PutBotPanel />
                        )}
                        {active === "Swing Bot" && (
                            <SwingBotPanel apiBase={API_BASE} token={getAuthToken()} />
                        )}
                        {active === "Stock Ignition Bot" && (
                            <StockIgnitionBotPanel apiBase={API_BASE} token={getAuthToken()} />
                        )}
                        {active === "Crypto Bot" && (
                            <CryptoBotPanel />
                        )}
                        {active === "Crypto Swing Bot" && (
                            <CryptoSwingBotPanel apiBase={API_BASE} token={getAuthToken()} />
                        )}
                        {active === "Crypto Ignition Bot" && (
                            <CryptoIgnitionBotPanel apiBase={API_BASE} token={getAuthToken()} />
                        )}
                        {active === "Risk Level" && (
                            <RiskLevel
                                settings={settings}
                                profiles={profiles}
                                busy={busy}
                                patch={patch}
                            />
                        )}
                        {active === "Smart Exit" && (
                            <SmartExitPanel
                                settings={settings}
                                busy={busy}
                                result={smartExitResult}
                                onSimulate={() => doSmartExit(false)}
                                onExecute={() => doSmartExit(true)}
                            />
                        )}
                        {active === "Live Status" && <LiveStatus status={status} />}
                        {active === "Logs" && <Logs logs={logs} />}
                        {active === "Diagnostics Export" && (
                            <DiagnosticsExportPanel userId={userId} />
                        )}
                        {active === "Kill Switch" && (
                            <KillSwitch enabled={enabled} busy={busy} doKill={doKill} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-800">
            <div className="text-sm text-gray-300">{label}</div>
            <div>{children}</div>
        </div>
    );
}

function Toggle({ value, onChange, disabled }) {
    return (
        <button
            disabled={disabled}
            onClick={() => onChange(!value)}
            className={`px-3 py-2 rounded-lg border text-sm ${value ? "bg-green-900/30 border-green-700" : "bg-gray-800 border-gray-700"
                }`}
        >
            {value ? "ON" : "OFF"}
        </button>
    );
}

function DebugSummary({ settings, status }) {
    if (!settings || !status) return null;

    const debug = status.debug || {};
    const watchlistCount =
        debug.watchlist_count ??
        status?.settings?.watchlist_count ??
        (Array.isArray(settings.watchlist) ? settings.watchlist.length : 0);

    const scanSource = debug.scan_source || (settings.scan_all ? "full_market" : "watchlist");
    const lastReason = debug.last_reason || status?.state?.last_reason || "-";
    const bestSymbol = debug.best_symbol || "-";
    const bestScore =
        debug.best_score === null || debug.best_score === undefined
            ? "-"
            : Number(debug.best_score).toFixed(2);

    return (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4 mb-4">
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-xs text-gray-400 mb-1">Watchlist Count</div>
                <div className="text-lg font-semibold">{watchlistCount}</div>
            </div>

            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-xs text-gray-400 mb-1">Scan Source</div>
                <div className="text-sm font-semibold break-words">{scanSource}</div>
            </div>

            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-xs text-gray-400 mb-1">Last Best Symbol</div>
                <div className="text-lg font-semibold">{bestSymbol}</div>
            </div>

            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-xs text-gray-400 mb-1">Last Best Score</div>
                <div className="text-lg font-semibold">{bestScore}</div>
            </div>

            <div className="md:col-span-4 bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-xs text-gray-400 mb-1">Last HOLD Reason</div>
                <div className="text-sm text-gray-200">{lastReason}</div>
            </div>
        </div>
    );
}


function activityBotLabel(row) {
    const source = String(row?.source || "").toLowerCase();
    const strategy = String(row?.strategy_tag || "").toLowerCase();
    const combined = `${source} ${strategy}`;
    if (combined.includes("auto_manage_exits") || combined.includes("auto_exit")) return "Auto Exit";
    if (combined.includes("targeted_bot") || combined.includes("target_bot")) return "Target Bot";
    if (combined.includes("swing_bot") || combined.includes("swing_call") || combined.includes("swing_put")) return "Swing Bot";
    if (combined.includes("put_bot") || combined.includes("bearish_put")) return "PUT Bot";
    if (combined.includes("crypto_swing") || combined.includes("crypto swing") || combined.includes("swing_crypto")) return "Crypto Swing Bot";
    if (combined.includes("crypto_target")) return "Target Crypto Bot";
    if (combined.includes("crypto")) return "Crypto Bot";
    if (combined.includes("manual")) return "Manual";
    if (combined.includes("parabolic") || combined.includes("regular") || combined.includes("bot")) return "Regular Bot";
    return source ? source.replaceAll("_", " ") : "Regular/Legacy Bot";
}

function formatActivityTime(value) {
    if (!value) return "-";
    const date = new Date(String(value).includes("T") ? value : `${String(value).replace(" ", "T")}Z`);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function TradeActivityPanel({ activity, refreshActivity, busy }) {
    const orders = Array.isArray(activity?.orders) ? activity.orders : [];
    const regularCount = orders.filter((row) => activityBotLabel(row) === "Regular Bot").length;
    const successful = orders.filter((row) => {
        const status = String(row?.status || "").toLowerCase();
        return ["success", "filled", "accepted", "submitted", "pending"].includes(status);
    }).length;

    return (
        <div className="mt-4 rounded-xl border border-gray-800 bg-gray-950/60 p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="font-semibold">Recent Bot Order Activity</div>
                    <div className="text-xs text-gray-400">
                        Shows which bot actually submitted each order. Regular Bot records found: {regularCount}.
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Pill>{orders.length} records</Pill>
                    <Pill>{successful} successful/pending</Pill>
                    <button
                        disabled={busy}
                        onClick={refreshActivity}
                        className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-xs disabled:opacity-50"
                    >
                        Refresh Orders
                    </button>
                </div>
            </div>

            {activity?.error ? (
                <div className="mt-3 rounded-lg border border-red-700/50 bg-red-950/30 p-3 text-sm text-red-300">
                    {activity.error}
                </div>
            ) : orders.length === 0 ? (
                <div className="mt-3 text-sm text-gray-400">
                    No recorded bot orders were found yet. This means no logged order can be confirmed from the database.
                </div>
            ) : (
                <div className="mt-3 max-h-80 overflow-auto rounded-lg border border-gray-800">
                    <table className="min-w-full text-xs">
                        <thead className="sticky top-0 bg-gray-900 text-gray-400">
                            <tr>
                                <th className="px-3 py-2 text-left">Time</th>
                                <th className="px-3 py-2 text-left">Bot</th>
                                <th className="px-3 py-2 text-left">Broker / Mode</th>
                                <th className="px-3 py-2 text-left">Order</th>
                                <th className="px-3 py-2 text-left">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.slice(0, 40).map((row) => {
                                const symbol = row.option_symbol || row.symbol || "-";
                                const status = String(row.status || "unknown");
                                const bad = ["error", "failed", "rejected"].some((x) => status.toLowerCase().includes(x));
                                return (
                                    <tr key={row.id} className="border-t border-gray-800 text-gray-200">
                                        <td className="px-3 py-2 whitespace-nowrap text-gray-400">{formatActivityTime(row.created_at)}</td>
                                        <td className="px-3 py-2 font-semibold text-yellow-200">{activityBotLabel(row)}</td>
                                        <td className="px-3 py-2">{row.broker || "-"} / {row.mode || "-"}</td>
                                        <td className="px-3 py-2 font-mono">
                                            {row.side || "-"} {row.qty ?? "-"} {symbol}
                                        </td>
                                        <td className={`px-3 py-2 ${bad ? "text-red-300" : "text-green-300"}`} title={row.error || ""}>
                                            {status}{row.error ? ` — ${String(row.error).slice(0, 100)}` : ""}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function TopScanPanel({ topScan, refreshTopScan, busy }) {
    const rows = topScan?.top_trades || [];
    const filters = topScan?.liquidity_filters || {};
    const tradableCount = topScan?.tradable_cache_count;

    return (
        <div className="mt-4 bg-gray-950 border border-gray-800 rounded-xl p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div>
                    <div className="text-sm font-semibold">Top 10 Scan Results</div>
                    <div className="text-xs text-gray-400">
                        Ranked after price, dollar-volume, volatility, and tradable-symbol safety checks.
                    </div>
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                    {tradableCount !== null && tradableCount !== undefined ? (
                        <Pill>{tradableCount} tradable cached</Pill>
                    ) : null}
                    <button
                        disabled={busy}
                        onClick={refreshTopScan}
                        className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-xs"
                    >
                        Refresh Top 10
                    </button>
                </div>
            </div>

            {topScan?.error ? (
                <div className="text-xs text-red-300 mb-2">{topScan.error}</div>
            ) : null}

            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-2">
                    <div className="text-[11px] text-gray-500">Min Price</div>
                    <div className="text-xs">${filters.min_price ?? "-"}</div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-2">
                    <div className="text-[11px] text-gray-500">Max Price</div>
                    <div className="text-xs">${filters.max_price ?? "-"}</div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-2">
                    <div className="text-[11px] text-gray-500">Min $ Volume</div>
                    <div className="text-xs">{formatCompact(filters.effective_min_dollar_volume)}</div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-2">
                    <div className="text-[11px] text-gray-500">Min Vol Ratio</div>
                    <div className="text-xs">{filters.min_volume_ratio ?? "-"}</div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-2">
                    <div className="text-[11px] text-gray-500">Max ATR%</div>
                    <div className="text-xs">{filters.max_atr_pct ?? "-"}</div>
                </div>
            </div>

            <div className="overflow-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-left text-gray-400 border-b border-gray-800">
                            <th className="py-2 pr-2">#</th>
                            <th className="py-2 pr-2">Ticker</th>
                            <th className="py-2 pr-2">Type</th>
                            <th className="py-2 pr-2">Score</th>
                            <th className="py-2 pr-2">Confidence</th>
                            <th className="py-2 pr-2">Price</th>
                            <th className="py-2 pr-2">Exp / Strike</th>
                            <th className="py-2 pr-2">Vol× / DTE</th>
                            <th className="py-2 pr-2">RSI / OI</th>
                            <th className="py-2 pr-2">Reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, idx) => {
                            const sm = r.signal_model || {};
                            return (
                                <tr key={`${r.symbol}-${idx}`} className="border-b border-gray-900">
                                    <td className="py-2 pr-2 text-gray-500">{idx + 1}</td>
                                    <td className="py-2 pr-2 font-semibold text-gray-100">{r.symbol || r.ticker}</td>
                                    <td className="py-2 pr-2">{r.asset_type === "option" ? `${r.strategy || "OPT"}` : "Equity"}</td>
                                    <td className="py-2 pr-2">{Number(r.score || 0).toFixed(1)}</td>
                                    <td className="py-2 pr-2">{Number(r.confidence || 0).toFixed(1)}%</td>
                                    <td className="py-2 pr-2">${Number(r.price || r.entryPrice || 0).toFixed(2)}</td>
                                    <td className="py-2 pr-2">{r.asset_type === "option" ? `${r.expiration || "-"} / ${r.strike || "-"}` : formatCompact(r.avg_dollar_volume_20)}</td>
                                    <td className="py-2 pr-2">{r.asset_type === "option" ? (r.dte ?? sm.dte ?? "-") : (sm.volume_ratio ?? "-")}</td>
                                    <td className="py-2 pr-2">{r.asset_type === "option" ? (r.open_interest ?? sm.open_interest ?? "-") : (sm.rsi ?? "-")}</td>
                                    <td className="py-2 pr-2 text-gray-400 max-w-[360px] truncate">{r.reason}</td>
                                </tr>
                            );
                        })}
                        {!rows.length ? (
                            <tr>
                                <td className="py-3 text-gray-500" colSpan={9}>
                                    No scan results yet. Click Refresh Top 10 or Run 1 Tick during market hours.
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function formatCompact(value) {
    const n = Number(value || 0);
    if (!Number.isFinite(n) || n <= 0) return "-";
    if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
}

function BotControl({
    settings,
    busy,
    supportedBrokers,
    patch,
    watchlistText,
    setWatchlistText,
    saveWatchlist,
}) {
    if (!settings) return <div className="text-sm text-gray-400">Loading…</div>;

    const watchCount = parseWatchlistText(watchlistText).length;

    return (
        <div className="space-y-2">
            <Row label="Bot Enabled">
                <Toggle
                    disabled={busy}
                    value={Boolean(settings.enabled)}
                    onChange={(v) => patch({ enabled: v })}
                />
            </Row>

            <Row label="Broker">
                <select
                    disabled={busy}
                    value={settings.broker}
                    onChange={(e) => patch({ broker: e.target.value })}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                >
                    {supportedBrokers.map((b) => (
                        <option key={b} value={b}>
                            {b === "alpaca" ? "alpaca — equities" : b === "tradier" ? "tradier — options" : b === "etrade" ? "E*TRADE — equities" : b}
                        </option>
                    ))}
                </select>
            </Row>

            <div className="text-xs text-blue-200 bg-blue-950/30 border border-blue-900 rounded-lg p-3">
                Broker routing: <strong>Alpaca</strong> and <strong>E*TRADE</strong> trade equities. <strong>Tradier</strong> trades options from the options cache.
                The broker dropdown can still be used for manual testing, but the cron can now run every enabled module below.
            </div>

            <div className="mt-4 bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div>
                        <div className="text-sm font-semibold">Automation Modules</div>
                        <div className="text-xs text-gray-400">
                            These toggles control what the Run 1 Tick cron and Auto Exit v2 cron are allowed to run.
                        </div>
                    </div>
                    <Pill>All-broker setup</Pill>
                </div>

                <Row label="Auto Buy Enabled">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.auto_buy_enabled ?? true)}
                        onChange={(v) => patch({ auto_buy_enabled: v })}
                    />
                </Row>

                <Row label="Equities Auto Buy">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.auto_buy_equities_enabled ?? true)}
                        onChange={(v) => patch({ auto_buy_equities_enabled: v })}
                    />
                </Row>

                <Row label="Options Auto Buy">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.auto_buy_options_enabled ?? true)}
                        onChange={(v) => patch({ auto_buy_options_enabled: v })}
                    />
                </Row>

                <Row label="Auto Exit v2 Enabled">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.auto_exit_enabled ?? true)}
                        onChange={(v) => patch({ auto_exit_enabled: v })}
                    />
                </Row>

                <Row label="Equities Auto Exit">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.auto_exit_equities_enabled ?? true)}
                        onChange={(v) => patch({ auto_exit_equities_enabled: v })}
                    />
                </Row>

                <Row label="Options Auto Exit">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.auto_exit_options_enabled ?? true)}
                        onChange={(v) => patch({ auto_exit_options_enabled: v })}
                    />
                </Row>
            </div>

            <div className="mt-4 bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div>
                        <div className="text-sm font-semibold">Broker Modules</div>
                        <div className="text-xs text-gray-400">
                            Turn on the broker modules that Auto Buy and Auto Exit v2 should include.
                        </div>
                    </div>
                    <Pill>Expandable</Pill>
                </div>

                <Row label="Alpaca Equities">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.broker_alpaca_enabled ?? true)}
                        onChange={(v) => patch({ broker_alpaca_enabled: v })}
                    />
                </Row>

                <Row label="Tradier Options">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.broker_tradier_enabled ?? true)}
                        onChange={(v) => patch({ broker_tradier_enabled: v })}
                    />
                </Row>

                <Row label="E*TRADE Equities">
                    <Toggle
                        disabled={busy}
                        value={Boolean(settings.broker_etrade_enabled ?? false)}
                        onChange={(v) => patch({ broker_etrade_enabled: v })}
                    />
                </Row>

                <div className="text-xs text-yellow-200 bg-yellow-950/20 border border-yellow-900 rounded-lg p-3 mt-3">
                    E*TRADE can now be enabled after you connect OAuth tokens. Alpaca remains your default equities broker; Tradier remains your options broker.
                </div>
            </div>

            <Row label="Scan All NYSE/Nasdaq">
                <Toggle
                    disabled={busy}
                    value={Boolean(settings.scan_all)}
                    onChange={(v) => patch({ scan_all: v })}
                />
            </Row>

            <Row label="Bot Lock Account (block manual trading)">
                <Toggle
                    disabled={busy}
                    value={Boolean(settings.lock_account)}
                    onChange={(v) => patch({ lock_account: v })}
                />
            </Row>

            <Row label="Paper Mode">
                <Toggle
                    disabled={busy}
                    value={Boolean(settings.paper_mode)}
                    onChange={(v) => patch({ paper_mode: v })}
                />
            </Row>

            <Row label="Allow Live Execution (extra gate)">
                <Toggle
                    disabled={busy}
                    value={Boolean(settings.allow_live)}
                    onChange={(v) => patch({ allow_live: v })}
                />
            </Row>

            {settings.global_live_entry_lockdown ? (
                <div className="mt-3 rounded-lg border border-red-700 bg-red-950/30 p-3 text-xs text-red-200">
                    Global live-entry lockdown is ON. New live buys are blocked for every bot; live Auto Exits remain active.
                </div>
            ) : null}

            <div className="mt-4 bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                    <div>
                        <div className="text-sm font-semibold">Watchlist Editor</div>
                        <div className="text-xs text-gray-400">
                            Used when Scan All is OFF. Paste commas, spaces, or one ticker per line.
                        </div>
                    </div>
                    <Pill>{watchCount} tickers</Pill>
                </div>

                <textarea
                    value={watchlistText}
                    onChange={(e) => setWatchlistText(e.target.value)}
                    disabled={busy}
                    rows={6}
                    placeholder="AAPL, NVDA, MSFT, META, AMZN"
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200"
                />

                <div className="mt-3 flex gap-2 flex-wrap">
                    <button
                        disabled={busy}
                        onClick={() => saveWatchlist(parseWatchlistText(watchlistText))}
                        className="px-3 py-2 rounded-lg bg-blue-900/30 border border-blue-700 text-sm"
                    >
                        Save Watchlist
                    </button>

                    <button
                        disabled={busy}
                        onClick={() => {
                            const preset = LIQUID_PRESET.join(", ");
                            setWatchlistText(preset);
                            saveWatchlist(LIQUID_PRESET);
                        }}
                        className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm"
                    >
                        Use Liquid Preset
                    </button>

                    <button
                        disabled={busy}
                        onClick={() => {
                            setWatchlistText("");
                            saveWatchlist([]);
                        }}
                        className="px-3 py-2 rounded-lg bg-red-900/30 border border-red-700 text-sm"
                    >
                        Clear Custom Watchlist
                    </button>
                </div>
            </div>

            <div className="text-xs text-gray-400 mt-3">
                Note: E*TRADE uses OAuth tokens that can expire daily, so reconnect it when status shows token expired.
            </div>
        </div>
    );
}

function RiskLevel({ settings, profiles, busy, patch }) {
    if (!settings) return <div className="text-sm text-gray-400">Loading…</div>;

    const keys = Object.keys(profiles || {});
    const selected = profiles?.[settings.risk_level];

    return (
        <div>
            <Row label="Risk Profile">
                <select
                    disabled={busy}
                    value={settings.risk_level}
                    onChange={(e) => patch({ risk_level: e.target.value })}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                >
                    {keys.map((k) => (
                        <option key={k} value={k}>
                            {profiles[k].label}
                        </option>
                    ))}
                </select>
            </Row>

            <div className="mt-4 bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-sm font-semibold mb-2">Profile Details</div>
                {selected ? (
                    <pre className="text-xs text-gray-300 whitespace-pre-wrap">
                        {JSON.stringify(selected, null, 2)}
                    </pre>
                ) : (
                    <div className="text-xs text-gray-500">No profile selected.</div>
                )}
            </div>
        </div>
    );
}


function SmartExitPanel({ settings, busy, result, onSimulate, onExecute }) {
    const mode = settings?.paper_mode ? "paper" : "live";
    const liveExecutionAllowed = Boolean(settings?.paper_mode || settings?.allow_live);
    const exits = result?.exits || [];
    const errors = result?.errors || [];

    return (
        <div className="space-y-4">
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-sm font-semibold">Smart Exit v2 — Profit Protection Engine</div>
                        <div className="text-xs text-gray-400 mt-1">
                            Checks whether each open trade is still healthy and whether it already made enough profit to protect the win.
                        </div>
                    </div>
                    <div className="flex gap-2 flex-wrap items-center">
                        <Pill>Mode: {mode}</Pill>
                        <Pill>Alpaca: {settings?.broker_alpaca_enabled ?? true ? "ON" : "OFF"}</Pill>
                        <Pill>Tradier: {settings?.broker_tradier_enabled ?? true ? "ON" : "OFF"}</Pill>
                        <Pill>E*TRADE: {settings?.broker_etrade_enabled ?? false ? "ON" : "OFF"}</Pill>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                        <div className="text-[11px] text-gray-500">Auto Exit v2</div>
                        <div className="text-xs">{settings?.auto_exit_enabled ?? true ? "Enabled" : "Disabled"}</div>
                    </div>
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                        <div className="text-[11px] text-gray-500">Equity Exits</div>
                        <div className="text-xs">{settings?.auto_exit_equities_enabled ?? true ? "Enabled" : "Disabled"}</div>
                    </div>
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                        <div className="text-[11px] text-gray-500">Option Exits</div>
                        <div className="text-xs">{settings?.auto_exit_options_enabled ?? true ? "Enabled" : "Disabled"}</div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                        <div className="text-[11px] text-gray-500">Tracks</div>
                        <div className="text-xs">Current P/L, Peak P/L, Giveback</div>
                    </div>
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                        <div className="text-[11px] text-gray-500">Protects</div>
                        <div className="text-xs">Dollar wins + green-to-red prevention</div>
                    </div>
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                        <div className="text-[11px] text-gray-500">Still Checks</div>
                        <div className="text-xs">Trend, RSI, stoch, sharp drops, DTE</div>
                    </div>
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                        <div className="text-[11px] text-gray-500">Exit Types</div>
                        <div className="text-xs">Take profit or cut loss</div>
                    </div>
                </div>

                <div className="flex gap-2 flex-wrap mt-4">
                    <button
                        disabled={busy}
                        onClick={onSimulate}
                        className="px-3 py-2 rounded-lg bg-blue-900/30 border border-blue-700 text-sm"
                    >
                        Smart Exit Simulate
                    </button>
                    <button
                        disabled={busy || !liveExecutionAllowed}
                        onClick={onExecute}
                        className="px-3 py-2 rounded-lg bg-red-900/40 border border-red-700 text-sm"
                    >
                        Smart Exit Execute
                    </button>
                </div>

                {!liveExecutionAllowed ? (
                    <div className="text-xs text-yellow-300 mt-3">
                        Live execution is blocked because Paper Mode is OFF and Allow Live Execution is OFF.
                    </div>
                ) : null}
            </div>

            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-sm font-semibold mb-3">Latest Smart Exit Result</div>
                {!result ? (
                    <div className="text-xs text-gray-500">No Smart Exit run yet. Click simulate first.</div>
                ) : (
                    <div className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                                <div className="text-[11px] text-gray-500">Executed</div>
                                <div className="text-xs">{String(Boolean(result.execute))}</div>
                            </div>
                            <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                                <div className="text-[11px] text-gray-500">Mode</div>
                                <div className="text-xs">{result.mode || mode}</div>
                            </div>
                            <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                                <div className="text-[11px] text-gray-500">Exit Count</div>
                                <div className="text-xs">{exits.length}</div>
                            </div>
                            <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                                <div className="text-[11px] text-gray-500">Errors</div>
                                <div className="text-xs">{errors.length}</div>
                            </div>
                        </div>

                        {errors.length ? (
                            <div className="bg-red-950/30 border border-red-900 rounded-lg p-3 text-xs text-red-200">
                                {errors.map((e, i) => <div key={i}>{e}</div>)}
                            </div>
                        ) : null}

                        <div className="overflow-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-left text-gray-400 border-b border-gray-800">
                                        <th className="py-2 pr-2">Broker</th>
                                        <th className="py-2 pr-2">Type</th>
                                        <th className="py-2 pr-2">Symbol</th>
                                        <th className="py-2 pr-2">Option</th>
                                        <th className="py-2 pr-2">Qty</th>
                                        <th className="py-2 pr-2">P/L $</th>
                                        <th className="py-2 pr-2">Peak $</th>
                                        <th className="py-2 pr-2">Giveback $</th>
                                        <th className="py-2 pr-2">Decision</th>
                                        <th className="py-2 pr-2">Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {exits.map((x, i) => (
                                        <tr key={`${x.symbol}-${i}`} className="border-b border-gray-900">
                                            <td className="py-2 pr-2">{x.broker}</td>
                                            <td className="py-2 pr-2">{x.asset_type}</td>
                                            <td className="py-2 pr-2 font-semibold">{x.symbol}</td>
                                            <td className="py-2 pr-2">{x.option_symbol || "-"}</td>
                                            <td className="py-2 pr-2">{x.qty}</td>
                                            <td className="py-2 pr-2">${Number(x.pnl_usd || 0).toFixed(2)}</td>
                                            <td className="py-2 pr-2">${Number(x.peak_profit_usd || 0).toFixed(2)}</td>
                                            <td className="py-2 pr-2">${Number(x.giveback_usd || 0).toFixed(2)}</td>
                                            <td className="py-2 pr-2">{x.decision}</td>
                                            <td className="py-2 pr-2 text-gray-400 max-w-[360px] truncate">{x.reason}</td>
                                        </tr>
                                    ))}
                                    {!exits.length ? (
                                        <tr>
                                            <td className="py-3 text-gray-500" colSpan={10}>
                                                No exits triggered. That usually means the positions are still within the hold rules.
                                            </td>
                                        </tr>
                                    ) : null}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function TargetedBotPanel({ settings, status, logs, result, busy, patch, runTick, resetState }) {
    const s = settings || {};
    const state = status?.state || result?.state || {};
    const openPositions = status?.positions || result?.positions || [];

    const numberPatch = (key) => (e) => patch({ [key]: Number(e.target.value) });

    return (
        <div className="space-y-4">
            <div className="bg-blue-950/20 border border-blue-900 rounded-xl p-4">
                <div className="text-sm font-semibold mb-1">Targeted Ticker Options Bot</div>
                <div className="text-xs text-gray-300">
                    This engine is Tradier options-only. It manages the exact OCC contract it opened, checks exits even when new entries are disabled, and waits for completed 5-minute plus 15-minute trend confirmation before buying.
                </div>
                {s.global_live_entry_lockdown ? (
                    <div className="mt-2 text-xs text-red-200">Global live-entry lockdown is ON; live exits are still protected.</div>
                ) : null}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-gray-950 border border-gray-800 rounded-xl p-4 space-y-2">
                    <Row label="Targeted Bot Enabled">
                        <Toggle disabled={busy} value={Boolean(s.enabled)} onChange={(v) => patch({ enabled: v })} />
                    </Row>
                    <Row label="Target Ticker">
                        <input disabled={busy} value={s.ticker || ""} onChange={(e) => patch({ ticker: e.target.value.toUpperCase() })} placeholder="AAPL" className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-32 text-right" />
                    </Row>
                    <Row label="Broker / Asset Type">
                        <span className="text-sm text-gray-300">Tradier — options only</span>
                    </Row>
                    <Row label="Direction">
                        <select disabled={busy} value={s.direction || "auto"} onChange={(e) => patch({ direction: e.target.value })} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm">
                            <option value="auto">Auto — require clear edge</option>
                            <option value="call">CALL only</option>
                            <option value="put">PUT only</option>
                        </select>
                    </Row>
                    <Row label="Paper Mode">
                        <Toggle disabled={busy} value={Boolean(s.paper_mode)} onChange={(v) => patch({ paper_mode: v })} />
                    </Row>
                    <Row label="Allow Live Entries">
                        <Toggle disabled={busy} value={Boolean(s.allow_live)} onChange={(v) => {
                            if (v && !window.confirm("Arm LIVE Targeted Bot entries? Keep the backend FORCE_PAPER_TRADING switch on until validation is complete.")) return;
                            patch({ allow_live: v });
                        }} />
                    </Row>
                    <Row label="Per-Trade Budget $">
                        <input disabled={busy} type="number" min="50" step="25" value={s.budget ?? 500} onChange={numberPatch("budget")} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-32 text-right" />
                    </Row>
                    <Row label="Entry Score Minimum">
                        <input disabled={busy} type="number" min="1" max="100" value={s.entry_score_min ?? 70} onChange={numberPatch("entry_score_min")} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-32 text-right" />
                    </Row>
                </div>

                <div className="bg-gray-950 border border-gray-800 rounded-xl p-4 space-y-2">
                    <Row label="Minimum DTE"><input disabled={busy} type="number" min="0" value={s.min_dte ?? 1} onChange={numberPatch("min_dte")} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-28 text-right" /></Row>
                    <Row label="Maximum DTE"><input disabled={busy} type="number" min="1" value={s.max_dte ?? 21} onChange={numberPatch("max_dte")} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-28 text-right" /></Row>
                    <Row label="Allow 0DTE"><Toggle disabled={busy} value={Boolean(s.allow_0dte)} onChange={(v) => patch({ allow_0dte: v })} /></Row>
                    <Row label="No 0DTE Buys After ET"><input disabled={busy} type="time" value={s.no_0dte_after_et || "11:00"} onChange={(e) => patch({ no_0dte_after_et: e.target.value })} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm" /></Row>
                    <Row label="Force 0DTE Exit After ET"><input disabled={busy} type="time" value={s.force_exit_0dte_after_et || "13:30"} onChange={(e) => patch({ force_exit_0dte_after_et: e.target.value })} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm" /></Row>
                    <Row label="Re-entry Cooldown Seconds"><input disabled={busy} type="number" min="0" value={s.reentry_cooldown_sec ?? 1200} onChange={numberPatch("reentry_cooldown_sec")} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-28 text-right" /></Row>
                    <Row label="Minimum Option Volume"><input disabled={busy} type="number" min="0" value={s.min_volume ?? 50} onChange={numberPatch("min_volume")} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-28 text-right" /></Row>
                    <Row label="Minimum Open Interest"><input disabled={busy} type="number" min="0" value={s.min_open_interest ?? 200} onChange={numberPatch("min_open_interest")} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-28 text-right" /></Row>
                    <Row label="Maximum Spread Ratio"><input disabled={busy} type="number" min="0.01" max="1" step="0.01" value={s.max_spread_pct ?? 0.18} onChange={numberPatch("max_spread_pct")} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm w-28 text-right" /></Row>
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                <button disabled={busy} onClick={() => runTick(false, false)} className="px-4 py-2 rounded-lg bg-blue-900/30 border border-blue-700 text-sm">Simulate Target Entry</button>
                <button disabled={busy} onClick={() => runTick(true, false)} className="px-4 py-2 rounded-lg bg-yellow-900/30 border border-yellow-700 text-sm">Execute Target Tick</button>
                <button disabled={busy} onClick={() => runTick(true, true)} className="px-4 py-2 rounded-lg bg-green-900/30 border border-green-700 text-sm">Exit Check Only</button>
                <button disabled={busy} onClick={resetState} className="px-4 py-2 rounded-lg bg-red-900/30 border border-red-700 text-sm">Reset Target State</button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                    <div className="text-sm font-semibold mb-2">Tracked State</div>
                    <pre className="text-xs text-gray-300 whitespace-pre-wrap">{JSON.stringify(state, null, 2)}</pre>
                </div>
                <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                    <div className="text-sm font-semibold mb-2">Open Target Positions</div>
                    <pre className="text-xs text-gray-300 whitespace-pre-wrap">{JSON.stringify(openPositions, null, 2)}</pre>
                </div>
            </div>

            <div className="bg-gray-950 border border-gray-800 rounded-xl overflow-hidden">
                <div className="p-3 text-sm font-semibold border-b border-gray-800">Targeted Bot Logs</div>
                <div className="max-h-72 overflow-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-gray-900"><tr><th className="p-2 text-left">Time</th><th className="p-2 text-left">Event</th><th className="p-2 text-left">Symbol</th><th className="p-2 text-left">Payload</th></tr></thead>
                        <tbody>
                            {(logs || []).map((l, i) => <tr key={l.id || i} className="border-t border-gray-900"><td className="p-2">{l.ts}</td><td className="p-2">{l.event}</td><td className="p-2">{l.symbol}</td><td className="p-2 text-gray-400 max-w-[520px] truncate">{l.payload_json}</td></tr>)}
                            {!logs?.length ? <tr><td className="p-3 text-gray-500" colSpan={4}>No targeted bot logs yet.</td></tr> : null}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function LiveStatus({ status }) {
    if (!status) return <div className="text-sm text-gray-400">Loading…</div>;
    const st = status.state || {};
    const s = status.settings || {};
    const d = status.debug || {};

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-sm font-semibold mb-2">Settings</div>
                <pre className="text-xs text-gray-300 whitespace-pre-wrap">
                    {JSON.stringify(s, null, 2)}
                </pre>
            </div>
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-sm font-semibold mb-2">State</div>
                <pre className="text-xs text-gray-300 whitespace-pre-wrap">
                    {JSON.stringify(st, null, 2)}
                </pre>
            </div>
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-sm font-semibold mb-2">Debug</div>
                <pre className="text-xs text-gray-300 whitespace-pre-wrap">
                    {JSON.stringify(d, null, 2)}
                </pre>
            </div>
        </div>
    );
}

function Logs({ logs }) {
    return (
        <div className="space-y-2">
            <div className="text-xs text-gray-400">Newest first</div>
            <div className="bg-gray-950 border border-gray-800 rounded-xl overflow-hidden">
                <div className="max-h-[520px] overflow-auto">
                    <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-900">
                            <tr className="text-left">
                                <th className="p-2 border-b border-gray-800">Time</th>
                                <th className="p-2 border-b border-gray-800">Level</th>
                                <th className="p-2 border-b border-gray-800">Event</th>
                                <th className="p-2 border-b border-gray-800">Symbol</th>
                                <th className="p-2 border-b border-gray-800">Payload</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(logs || []).map((l) => (
                                <tr key={l.id} className="border-b border-gray-900">
                                    <td className="p-2 text-gray-300">{l.ts}</td>
                                    <td className="p-2 text-gray-300">{l.level}</td>
                                    <td className="p-2 text-gray-200">{l.event}</td>
                                    <td className="p-2 text-gray-200">{l.symbol}</td>
                                    <td className="p-2 text-gray-400">
                                        <div className="max-w-[520px] truncate">{l.payload_json}</div>
                                    </td>
                                </tr>
                            ))}
                            {!logs?.length ? (
                                <tr>
                                    <td className="p-3 text-gray-500" colSpan={5}>
                                        No logs yet.
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}


const DIAGNOSTIC_SECTION_OPTIONS = [
    {
        id: "regular_bot_logs",
        label: "Regular Bot Logs",
        description: "Candidate scores, entry decisions, rejections, sizing, and Regular Bot state.",
    },
    {
        id: "auto_exit_logs",
        label: "Auto Exit Logs",
        description: "Exit decisions, broker reconciliation, profit protection, and loss exits.",
    },
    {
        id: "put_bot_logs",
        label: "PUT Bot Logs",
        description: "Bearish scans, contract selection, entry discipline, and PUT exits.",
    },
    {
        id: "target_bot_logs",
        label: "Target Bot Logs",
        description: "Targeted ticker signals, contract truth, cooldowns, entries, and exits.",
    },
    {
        id: "swing_bot_logs",
        label: "Swing Bot Logs",
        description: "Swing CALL/PUT scans, multi-timeframe scores, contract selection, entries, exits, and tracked Swing positions.",
    },
    {
        id: "crypto_bot_logs",
        label: "Crypto Bot Logs",
        description: "Market and target crypto scans, exposure, entries, exits, and circuit breakers.",
    },
    {
        id: "crypto_swing_bot_logs",
        label: "Crypto Swing Bot Logs",
        description: "Daily/4h/1h/30m Swing scans, shadow/live trades, thesis holds, high-water protection, and exits.",
    },
    {
        id: "stock_ignition_bot_logs",
        label: "Stock Ignition Bot Logs",
        description: "Ignition scans, compression/base detection, breakout/retest signals, CALL/PUT contract selection, entries, exits, and rejection reasons.",
    },
    {
        id: "crypto_ignition_bot_logs",
        label: "Crypto Ignition Bot Logs",
        description: "1m/5m/15m ignition scans, volume acceleration, breakout/retest signals, execution checks, entries, exits, and rejection reasons.",
    },
    {
        id: "recent_order_activity",
        label: "Recent Bot Order Activity",
        description: "Order-forensics rows showing which bot submitted each broker order.",
    },
    {
        id: "tracked_state",
        label: "Tracked State",
        description: "Regular, Target, PUT, Swing, Stock Ignition, Crypto, Crypto Ignition, risk, and position state snapshots.",
    },
    {
        id: "trade_lifecycle",
        label: "Trade Lifecycle CSV",
        description: "Trade log and auto-trade lifecycle rows with realized P/L and broker order IDs.",
    },
    {
        id: "settings_and_build",
        label: "Settings & Build Info",
        description: "Non-secret bot settings, table inventory, and server build metadata.",
    },
];

function localDateInputValue(date = new Date()) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function formatDiagnosticBytes(value) {
    const n = Number(value || 0);
    if (!Number.isFinite(n) || n <= 0) return "0 B";
    if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
    if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${Math.round(n)} B`;
}

function DiagnosticsExportPanel({ userId }) {
    const [selected, setSelected] = useState(() =>
        Object.fromEntries(DIAGNOSTIC_SECTION_OPTIONS.map((item) => [item.id, true]))
    );
    const [startDate, setStartDate] = useState(() => localDateInputValue());
    const [endDate, setEndDate] = useState(() => localDateInputValue());
    const [mode, setMode] = useState("all");
    const [catalog, setCatalog] = useState(null);
    const [loadingCatalog, setLoadingCatalog] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const selectedIds = DIAGNOSTIC_SECTION_OPTIONS
        .filter((item) => selected[item.id])
        .map((item) => item.id);

    const allSelected = selectedIds.length === DIAGNOSTIC_SECTION_OPTIONS.length;
    const noneSelected = selectedIds.length === 0;

    const loadCatalog = async () => {
        if (!userId) return;
        setLoadingCatalog(true);
        setError("");
        try {
            const data = await getAdminDiagnosticsCatalog(userId);
            setCatalog(data || {});
        } catch (e) {
            setCatalog(null);
            setError(e?.message || "Could not load the diagnostics catalog.");
        } finally {
            setLoadingCatalog(false);
        }
    };

    useEffect(() => {
        loadCatalog();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const toggleAll = () => {
        const next = !allSelected;
        setSelected(
            Object.fromEntries(DIAGNOSTIC_SECTION_OPTIONS.map((item) => [item.id, next]))
        );
    };

    const download = async () => {
        if (!userId) {
            setError("Missing user session. Please log in again.");
            return;
        }
        if (noneSelected) {
            setError("Select at least one diagnostics section.");
            return;
        }
        if (startDate && endDate && startDate > endDate) {
            setError("Start date must be on or before end date.");
            return;
        }

        setDownloading(true);
        setError("");
        setMessage("");
        try {
            const result = await downloadAdminDiagnosticsZip(userId, {
                sections: selectedIds,
                start_date: startDate,
                end_date: endDate,
                mode,
                max_rows_per_table: 10000,
            });

            const url = window.URL.createObjectURL(result.blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = result.filename || "mystockpicksai_diagnostics.zip";
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);

            setMessage(
                `Downloaded ${result.filename} (${formatDiagnosticBytes(result.size)}).`
            );
        } catch (e) {
            setError(e?.message || "Diagnostics download failed.");
        } finally {
            setDownloading(false);
        }
    };

    const catalogSections = catalog?.sections || {};

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-yellow-800/70 bg-yellow-950/20 p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-lg font-semibold text-yellow-200">
                            Admin Diagnostics Export
                        </div>
                        <div className="mt-1 text-xs text-gray-300 max-w-3xl">
                            Build one redacted ZIP containing the exact bot logs, tracked state,
                            broker-order lifecycle, and settings needed to audit a trading day.
                            The backend binds the export to the authenticated account and refuses
                            non-admin requests.
                        </div>
                    </div>
                    <div className="flex gap-2 items-center flex-wrap">
                        <Pill>{selectedIds.length} selected</Pill>
                        <Pill>{catalog?.admin ? "ADMIN VERIFIED" : "ADMIN CHECK"}</Pill>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 rounded-xl border border-gray-800 bg-gray-950 p-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <div>
                            <div className="text-sm font-semibold">Files to include</div>
                            <div className="text-xs text-gray-400">
                                Select only what you need, or use Select All for a full-day audit.
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={toggleAll}
                            className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-xs"
                        >
                            {allSelected ? "Clear All" : "Select All"}
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {DIAGNOSTIC_SECTION_OPTIONS.map((item) => {
                            const availableTables = catalogSections?.[item.id]?.tables || [];
                            const checked = Boolean(selected[item.id]);
                            return (
                                <label
                                    key={item.id}
                                    className={
                                        "rounded-lg border p-3 cursor-pointer transition " +
                                        (checked
                                            ? "border-yellow-700 bg-yellow-950/20"
                                            : "border-gray-800 bg-gray-900/40 hover:border-gray-700")
                                    }
                                >
                                    <div className="flex items-start gap-3">
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={(e) =>
                                                setSelected((current) => ({
                                                    ...current,
                                                    [item.id]: e.target.checked,
                                                }))
                                            }
                                            className="mt-1 h-4 w-4"
                                        />
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-gray-100">
                                                {item.label}
                                            </div>
                                            <div className="mt-1 text-[11px] text-gray-400">
                                                {item.description}
                                            </div>
                                            {catalog && (
                                                <div className="mt-1 text-[10px] text-gray-500 break-words">
                                                    Detected tables: {availableTables.length
                                                        ? availableTables.join(", ")
                                                        : "none yet / generated summary"}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                </div>

                <div className="rounded-xl border border-gray-800 bg-gray-950 p-4 space-y-4">
                    <div>
                        <div className="text-sm font-semibold">Export filters</div>
                        <div className="text-xs text-gray-400">
                            Dates use the trading day shown in stored timestamps.
                        </div>
                    </div>

                    <label className="block text-xs text-gray-300">
                        Start date
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm"
                        />
                    </label>

                    <label className="block text-xs text-gray-300">
                        End date
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm"
                        />
                    </label>

                    <label className="block text-xs text-gray-300">
                        Trading mode
                        <select
                            value={mode}
                            onChange={(e) => setMode(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm"
                        >
                            <option value="all">Paper + Live</option>
                            <option value="paper">Paper only</option>
                            <option value="live">Live only</option>
                        </select>
                    </label>

                    <button
                        type="button"
                        onClick={download}
                        disabled={downloading || noneSelected || !catalog?.admin}
                        className="w-full px-4 py-3 rounded-lg bg-yellow-500 text-black font-semibold text-sm hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {downloading ? "Building ZIP…" : "Download Selected ZIP"}
                    </button>

                    <button
                        type="button"
                        onClick={loadCatalog}
                        disabled={loadingCatalog}
                        className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-xs disabled:opacity-50"
                    >
                        {loadingCatalog ? "Checking backend…" : "Refresh Diagnostics Check"}
                    </button>

                    <div className="text-[10px] text-gray-500">
                        Secrets are redacted server-side. The ZIP still contains private account
                        and trading information, so store and share it carefully.
                    </div>
                </div>
            </div>

            {error ? (
                <div className="rounded-lg border border-red-800 bg-red-950/30 p-3 text-sm text-red-300">
                    {error}
                </div>
            ) : null}

            {message ? (
                <div className="rounded-lg border border-green-800 bg-green-950/30 p-3 text-sm text-green-300">
                    {message}
                </div>
            ) : null}

            {catalog?.warnings?.length ? (
                <div className="rounded-lg border border-amber-900 bg-amber-950/20 p-3 text-xs text-amber-200">
                    {catalog.warnings.map((warning, index) => (
                        <div key={index}>{warning}</div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function KillSwitch({ enabled, busy, doKill }) {
    return (
        <div className="space-y-3">
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-sm font-semibold mb-2">Emergency Stop</div>
                <div className="text-xs text-gray-400 mb-3">
                    Disables bot execution immediately. (Liquidation is not included in this MVP—add it later once your broker adapters are fully validated.)
                </div>
                <button
                    disabled={busy || !enabled}
                    onClick={() => doKill(true)}
                    className="px-4 py-2 rounded-lg bg-red-900/40 border border-red-700 text-sm"
                >
                    Kill Switch (Disable Bot)
                </button>
            </div>
        </div>
    );
}